<template>
    <h3 class="pb-1">{{ props.isPopup ? 'Quick Enquiry' : 'Bring Your Vision to Life' }}</h3>
    <p class="pb-2 text-muted">
        {{ props.isPopup ? 'Tell us about your project.' : 'Ready to turn your concept into a digital reality? Reach out to our team to explore how we can build something extraordinary together.' }}
    </p>

    <form v-if="!formSubmitted" id="ContactForm" @submit.prevent="validate" action="#" class="position-relative">
        <Spinner :isAbsolute="true" :visible="isSubmitting" />
        <div class="row">
            <div class="col-md-6 form-group pb-3 mb-2">
                <input ref="nameInput" type="text" class="form-control" id="form-name" name="name" placeholder="Name *"
                    required v-model="formData.name" />
                <i v-if="formValidate.name" class="text-danger mb-2">{{ formValidate.name }}</i>
            </div>
            <div class="col-md-6 form-group pb-3 mb-2">
                <input type="tel" class="form-control" id="form-subject" name="telephone" placeholder="Telephone *"
                    v-model="formData.telephone" />
                <i v-if="formValidate.telephone" class="text-danger mb-2">{{ formValidate.telephone }}</i>
            </div>
        </div>
        <div class="form-group pb-3 mb-2">
            <input type="email" class="form-control" id="form-email" name="email" placeholder="Email Address *"
                required v-model="formData.email" />
                <i v-if="formValidate.email" class="text-danger mb-2">{{ formValidate.email }}</i>
        </div>
        <div class="form-group pb-3 mb-2">
            <textarea class="form-control" id="form-message" name="message" placeholder="Message *"
                required v-model="formData.message">
            </textarea>
            <i v-if="formValidate.message" class="text-danger mb-2">{{ formValidate.message }}</i>
        </div>
        
        <button class="btn btn-dark text-white py-3 px-5 mt-4 w-100" type="submit">
            Submit Enquiry
        </button>
        
        <div v-if="formError" class="alert alert-danger mt-3">
            {{ formError }}
        </div>
    </form>
    
    <div v-else-if="!props.isPopup" class="alert alert-success mt-3">
        Thank you for contacting us, we will get back to you shortly.
    </div>
</template>

<script setup lang="ts">
    import { ref, Ref, onMounted, defineProps, defineEmits } from "vue"
    import { IReCaptchaComposition, useReCaptcha } from 'vue-recaptcha-v3'
    import router from "@/router"
    import Spinner from "@/components/general/Spinner.vue";

    const props = defineProps({
      isPopup: {
        type: Boolean,
        default: false
      }
    });

    const emit = defineEmits(['form-submitted']);

    var formData: Ref<any> = ref({
        name: "",
        telephone: "",
        email: "",
        message: ""
    })
    var formValidate: Ref<any> = ref({
        name: "",
        telephone: "",
        email: "",
        message: ""
    })
    const nameInput: Ref<any> = ref()
    var recapthcaObject: Ref<any | undefined> = ref(undefined)
    var isSubmitting: Ref<boolean> = ref(false)
    var formSubmitted: Ref<boolean> = ref(false)
    var formError: Ref<string | undefined> = ref(undefined)

    async function recaptcha(callback: any) {
      if (recapthcaObject.value) {
        const token = await recapthcaObject.value.executeRecaptcha(`${process.env.VUE_APP_FORM_KEY_CONTACT}`)
        callback(token)
      }
    }

    function validate () {
        var isValid = true
        Object.values(document.querySelectorAll("#ContactForm [required]")).forEach((input: any) => {
            const key = input.name
            const value = formData.value[key]
            if (value == null || value == "") {
                formValidate.value[key] = "This field is required."
                document.querySelector(`[name*='${key}']`)?.classList.add("invalid-flag")
                isValid = false
            }
            else {
                formValidate.value[key] = ""
                document.querySelector(`[name*='${key}']`)?.classList.remove("invalid-flag")
            }
        })
        if (!isValid) {
            return false
        }

        isSubmitting.value = true
        recaptcha((token: any) => {formPost(token)})
    }
    
    async function formPost (recaptchaResult: string) {
        const FormContent = formData.value
        const formKey = process.env.VUE_APP_FORM_KEY_CONTACT || ""
        const endpoint = process.env.VUE_APP_FORM_ENDPOINT ? process.env.VUE_APP_FORM_ENDPOINT+ "/api/forms/" + formKey : ""
        const store_only = process.env.VUE_APP_FORM_STORE_ONLY === "true" ? true : false
        const email = formData.value.email

        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                "GoogleRecaptchaResponse": recaptchaResult,
                FormContent,
                "StoreOnly": store_only,
                "EmailAddress": email
            })
        }

        formError.value = ""
        let res: any
        try {
            res = await fetch(endpoint, options)
                .then(result => {
                    if (result.status === 200) {
                        isSubmitting.value = false
                        formSubmitted.value = true
                        
                        if (props.isPopup) {
                            emit('form-submitted', true);
                            formData.value = { name: "", telephone: "", email: "", message: "" };
                        }

                        return result.json().then((data) => { /*Ref = data.resultData;*/ })
                    }
                    else {
                        isSubmitting.value = false
                        formError.value = 'Error submitting data.'
                        if (props.isPopup) {
                            emit('form-submitted', false);
                        }
                    }
                })
        } catch (e: any) {
            isSubmitting.value = false
            formError.value = `Error submitting data. ${e}`
            if (props.isPopup) {
                emit('form-submitted', false);
            }
            throw new Error(e)
        }
    }
    onMounted(async () => {
        recapthcaObject.value = useReCaptcha() as IReCaptchaComposition
        
        if (!props.isPopup) {
            setTimeout(() => {
                if (router.currentRoute.value.hash.includes("ContactUs") && nameInput.value != null) {
                    nameInput.value.focus()
                }
            }, 200)
        }
    })
</script>

<style scoped lang="scss">
    .form-control {
        border-radius: 0px;
        border: none;
        border-bottom: 1px solid #bbb;
        background-color: transparent;
        &.invalid-flag {
            border-left-color: #dc3545;
        }
        &:focus {
            border-color: $dark;
            background-color: transparent;
            box-shadow: inset 0 0 0 transparent,0 .5rem 1.125rem -.5rem #6366f133;
        }
    }
</style>